import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  Upload,
  InputNumber,
  Switch,
  Menu,
  Dropdown,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  DownOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCategories } from "../../hooks/useCategories";
import { useVendors } from "../../hooks/useVendors";
import { Product } from "../../hooks/useProducts";
import {
  ProductFormValues,
  productSchema,
} from "../../validations/productSchema";
import { useLocations } from "../../hooks/useLocation";

const { Option } = Select;

interface AddProductsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  product?: Product | null;
  canSeeAllLocations: boolean; // New prop for location permission
  userLocationId?: number; // User's location ID
}

const AddProductsModal: React.FC<AddProductsModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  product,
  canSeeAllLocations,
  userLocationId,
}) => {
  const { data: categories } = useCategories();
  const { data: vendors, isLoading: isLoadingVendors } = useVendors(1, {});
  const { data: locations, isLoading: isLoadingLocations } = useLocations();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<
    number | null
  >(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    product?.images?.[0]?.url || null
  );

  // State for multiple location selection - only allow if user can see all locations
  const [allLocations, setAllLocations] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      status: true,
      qty: 1,
      amount: 0,
      discount: 0,
    },
  });

  // Pre-fill form fields when product is provided (modify mode)
  useEffect(() => {
    if (visible && product) {
      // Handle API inconsistency where status is sent as number but typed as boolean
      // If status is a number (from API), convert 1 to true (available) and 0 to false (unavailable)
      // If status is already a boolean (to match the interface), use it directly
      const statusValue =
        typeof product.status === "number"
          ? product.status === 1
          : !!product.status;

      // Try to find the parent category for this product's category
      if (categories?.data && product.category) {
        const parentCategory = categories.data.find((cat) =>
          cat.children?.some((child) => child.child_id === product.category.id)
        );

        if (parentCategory) {
          setSelectedCategoryId(parentCategory.id);
          setSelectedSubcategoryId(product.category.id);
        }
      }

      // Handle location selection based on permissions
      let locationValue: string[];
      if (canSeeAllLocations) {
        // User can see all locations - use product's location or allow multiple
        locationValue = [product.location_id.toString()];
        setAllLocations(false);
      } else {
        // User can't see all locations - force their location
        locationValue = userLocationId ? [userLocationId.toString()] : [];
        setAllLocations(false);
      }

      reset({
        name: product.name,
        description: product.description,
        qty: product.qty,
        amount: product.amount,
        discount: product.discount,
        status: statusValue, // Use the determined value
        category_id: product.category?.id?.toString() || "", // Convert to string
        vendor_id: product.vendor_id.toString(),
        subcategory_id: product.category?.id || 0,
        location_id: locationValue,
      });
      setSelectedCategoryId(product.category?.id || null);
      setSelectedSubcategoryId(product.category?.id || null);
      setImagePreview(product.images?.[0]?.url || null);
    } else {
      // New product - set default location based on permissions
      const defaultLocationValue = canSeeAllLocations
        ? []
        : userLocationId
        ? [userLocationId.toString()]
        : [];

      reset({
        status: true,
        qty: 1,
        amount: 0,
        discount: 0,
        location_id: defaultLocationValue,
      });
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
      setImagePreview(null);
      setAllLocations(false);
    }
    // eslint-disable-next-line
  }, [visible, product, reset, canSeeAllLocations, userLocationId]);

  const onFormSubmit = async (data: ProductFormValues) => {
    // Handle "All Locations" selection
    let locationIdsToSubmit: number[] = [];

    if (canSeeAllLocations) {
      if (allLocations) {
        // Empty array means all locations in the API
        locationIdsToSubmit = locations?.map((loc) => loc.id) || [];
      } else if (data.location_id && data.location_id.length > 0) {
        // Convert location IDs to numbers for the API
        locationIdsToSubmit = data.location_id.map((id) => Number(id));
      }
    } else {
      // Force user's location for restricted users
      locationIdsToSubmit = userLocationId ? [userLocationId] : [];
    }

    const transformedData = {
      category_id: Number(data.subcategory_id),
      location_id: locationIdsToSubmit, // Now submitting an array of location IDs
      vendor_id: Number(data.vendor_id),
      name: data.name,
      description: data.description,
      qty: Number(data.qty),
      // Convert boolean to appropriate value based on API expectations
      // The API expects 1 for available, 0 for unavailable
      status: data.status ? 1 : 0,
      amount: Number(data.amount),
      slug: data.name.toLowerCase().replace(/\s+/g, "-"),
      discount: Number(data.discount),
      imageFile: imageFile, // Pass the image file
    };

    if (product) {
      // For updating a product
      onSubmit({ ...transformedData, id: product.id });
    } else {
      // For creating a new product
      onSubmit(transformedData);
    }

    reset();
    setImageFile(null);
    setImagePreview(null);
    onCancel();
  };

  const handleImageUpload = (file: File) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setValue("images", file);
    return false;
  };

  // Category dropdown menu renderer
  const renderCategoryMenu = () => {
    if (!categories?.data) return <Menu></Menu>;

    return (
      <Menu
        onClick={(e) => {
          // Split the key which contains both parent and child IDs
          const [parentId, childId] = e.key.split("-").map(Number);
          setSelectedCategoryId(parentId);
          setSelectedSubcategoryId(childId);
          setValue("category_id", parentId.toString());
          setValue("subcategory_id", childId);
        }}
      >
        {categories.data.map((category) => (
          <Menu.SubMenu key={`sub-${category.id}`} title={category.name}>
            {category.children?.map((subcat) => (
              <Menu.Item key={`${category.id}-${subcat.child_id}`}>
                {subcat.name}
              </Menu.Item>
            ))}
          </Menu.SubMenu>
        ))}
      </Menu>
    );
  };

  // Get selected category/subcategory display text
  const getSelectedCategoryText = () => {
    // First priority: Check if we have already selected a category
    if (selectedCategoryId && selectedSubcategoryId) {
      const parentCategory = categories?.data?.find(
        (cat) => cat.id === selectedCategoryId
      );

      const subCategory = parentCategory?.children?.find(
        (sub) => sub.child_id === selectedSubcategoryId
      );

      if (parentCategory && subCategory) {
        return `${parentCategory.name} > ${subCategory.name}`;
      }
    }

    // Second priority: If editing a product, show its category
    if (product && product.category) {
      // Directly display the product's category name
      return product.category.name || "Select category";
    }

    // Default
    return "Select category";
  };

  // Handle the "All Locations" checkbox change - only if user can see all locations
  const handleAllLocationsChange = (checked: boolean) => {
    if (!canSeeAllLocations) return; // Prevent change if user can't see all locations

    setAllLocations(checked);

    if (checked) {
      // Clear location_id when "All Locations" is checked
      setValue("location_id", locations?.map((loc) => loc.id.toString()) || []);
    }
  };

  // For required field indicator
  const RequiredMark = () => <span className="text-red-500">*</span>;

  // Get filtered locations for display
  const availableLocations = canSeeAllLocations
    ? locations
    : locations?.filter((loc) => loc.id === userLocationId);

  return (
    <Modal
      title={
        <span className="font-bold text-lg">
          {product ? "Edit Product" : "Add Product"}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={720}
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        {/* Product Name */}
        <div>
          <label className="text-gray-700 text-sm font-medium">
            Product Name <RequiredMark />
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter product name"
                status={errors.name ? "error" : ""}
                className="mt-1"
              />
            )}
          />
          {errors.name && (
            <span className="text-red-500 text-sm">{errors.name.message}</span>
          )}
        </div>

        {/* Category Selection with Dropdown Menu */}
        <div>
          <label className="text-gray-700 text-sm font-medium">
            Category <RequiredMark />
          </label>
          <div className="mt-1">
            <Dropdown overlay={renderCategoryMenu()} trigger={["click"]}>
              <Button className="w-full flex justify-between items-center">
                <span>{getSelectedCategoryText()}</span>
                <DownOutlined />
              </Button>
            </Dropdown>
            {(errors.category_id || errors.subcategory_id) && (
              <span className="text-red-500 text-sm">
                Please select a category and subcategory
              </span>
            )}
          </div>
          {/* Hidden controllers to store the values */}
          <Controller
            name="category_id"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Controller
            name="subcategory_id"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
        </div>

        {/* Vendor Selection */}
        <div>
          <label className="text-gray-700 text-sm font-medium">
            Vendor <RequiredMark />
          </label>
          <Controller
            name="vendor_id"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select vendor"
                className="w-full mt-1"
                status={errors.vendor_id ? "error" : ""}
                loading={isLoadingVendors}
              >
                {vendors?.data.map((vendor) => (
                  <Option key={vendor.id} value={vendor.id.toString()}>
                    {vendor.name}
                  </Option>
                ))}
              </Select>
            )}
          />
          {errors.vendor_id && (
            <span className="text-red-500 text-sm">
              {errors.vendor_id.message}
            </span>
          )}
        </div>

        {/* Product Description */}
        <div>
          <label className="text-gray-700 text-sm font-medium">
            Description <RequiredMark />
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                rows={4}
                placeholder="Enter product description"
                status={errors.description ? "error" : ""}
                className="mt-1"
              />
            )}
          />
          {errors.description && (
            <span className="text-red-500 text-sm">
              {errors.description.message}
            </span>
          )}
        </div>

        {/* Quantity & Price */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="text-gray-700 text-sm font-medium">
              Quantity <RequiredMark />
            </label>
            <Controller
              name="qty"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  min={1}
                  className="w-full mt-1"
                  status={errors.qty ? "error" : ""}
                  onChange={(value) => field.onChange(Number(value))} // Ensure value is a number
                />
              )}
            />
            {errors.qty && (
              <span className="text-red-500 text-sm">{errors.qty.message}</span>
            )}
          </div>

          <div className="flex-1">
            <label className="text-gray-700 text-sm font-medium">
              Price <RequiredMark />
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  min={0.01}
                  step={0.01}
                  className="w-full mt-1"
                  status={errors.amount ? "error" : ""}
                  formatter={(value) =>
                    `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  onChange={(value) => field.onChange(Number(value))} // Ensure value is a number
                />
              )}
            />
            {errors.amount && (
              <span className="text-red-500 text-sm">
                {errors.amount.message}
              </span>
            )}
          </div>
        </div>

        {/* Discount */}
        <div>
          <label className="text-gray-700 text-sm">Discount (%)</label>
          <Controller
            name="discount"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                min={0}
                max={100}
                className="w-full mt-1"
                status={errors.discount ? "error" : ""}
                formatter={(value) => `${value}%`}
                onChange={(value) => field.onChange(Number(value))} // Ensure value is a number
              />
            )}
          />
          {errors.discount && (
            <span className="text-red-500 text-sm">
              {errors.discount.message}
            </span>
          )}
        </div>

        {/* Location Selection - Updated to support multiple locations with permission control */}
        <div>
          <div className="flex justify-between items-center">
            <label className="text-gray-700 text-sm font-medium">
              Location(s) <RequiredMark />
            </label>
            {/* Only show "All Locations" toggle if user can see all locations */}
            {canSeeAllLocations && (
              <div className="flex items-center">
                <Switch
                  checked={allLocations}
                  onChange={handleAllLocationsChange}
                  size="small"
                />
                <span className="ml-2 text-sm text-gray-600">
                  All Locations
                </span>
              </div>
            )}
          </div>

          <Controller
            name="location_id"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                mode={canSeeAllLocations ? "multiple" : "multiple"} // Keep multiple for consistency
                placeholder={
                  canSeeAllLocations ? "Select location(s)" : "Location"
                }
                className="w-full mt-1"
                status={errors.location_id ? "error" : ""}
                loading={isLoadingLocations}
                disabled={allLocations || !canSeeAllLocations} // Disable if all locations selected OR user can't see all locations
                onChange={(value) => field.onChange(value)}
              >
                {availableLocations?.map((location) => (
                  <Option key={location.id} value={location.id.toString()}>
                    {location.name}
                  </Option>
                ))}
              </Select>
            )}
          />
          {errors.location_id && (
            <span className="text-red-500 text-sm">
              {errors.location_id.message}
            </span>
          )}
          {allLocations && canSeeAllLocations && (
            <div className="mt-1 text-xs text-blue-600">
              This product will be available in all locations
            </div>
          )}
          {!canSeeAllLocations && (
            <div className="mt-1 text-xs text-gray-500">
              Product will be available in your location only
            </div>
          )}
        </div>

        {/* Availability Toggle */}
        <div>
          <label className="text-gray-700 text-sm font-medium flex items-center">
            Availability <RequiredMark />
            <Tooltip title="Toggle ON for Available, OFF for Unavailable ">
              <QuestionCircleOutlined className="ml-1 text-gray-400" />
            </Tooltip>
          </label>
          <div className="mt-2 flex items-center">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                  checkedChildren="Available"
                  unCheckedChildren="Unavailable"
                />
              )}
            />
            <span className="ml-2 text-sm">
              {watch("status") ? "Available" : "Unavailable"}
            </span>
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="text-gray-700 text-sm">
            Product Image {!product && <RequiredMark />}
          </label>
          <Upload
            beforeUpload={handleImageUpload}
            showUploadList={false}
            className="w-full mt-1"
          >
            <Button icon={<UploadOutlined />} className="w-full">
              Click to {imagePreview ? "Change" : "Upload"} Image
            </Button>
          </Upload>
          {imagePreview && (
            <div className="mt-2 relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="h-24 w-24 object-contain border rounded"
              />
              <Button
                type="text"
                danger
                size="small"
                className="absolute top-0 right-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setImagePreview(null);
                  setImageFile(null);
                }}
              >
                Remove
              </Button>
            </div>
          )}
          {errors.images && (
            <span className="text-red-500 text-sm">
              {errors.images.message}
            </span>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={onCancel}
            className="border border-gray-300 px-6 py-2 rounded-lg"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {product ? "Update Product" : "Add Product"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddProductsModal;
